import {MithrilComponent} from '@bitstillery/mithril'
import {Icon, Spinner} from '@bitstillery/common/components'
import {classes} from '@bitstillery/common/lib/utils'
import {state} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'

export interface CollectionStatusAttrs {
    className?: string
    collection: CollectionProxy
    /**
     * What this row is waiting for, in the words of whatever it lists — "Loading products". Shown in
     * place of a count until there is one, since a count of zero reads as an empty result rather than
     * as an unfinished one.
     */
    loading?: string
    virtualize?: boolean
}

interface CollectionStatusData {
    jump_input: number | null
}

export class CollectionStatus extends MithrilComponent<CollectionStatusAttrs> {
    data: CollectionStatusData = state(
        {
            jump_input: null,
        },
        'collection-status-data',
    )

    view(vnode: m.Vnode<CollectionStatusAttrs>) {
        const {state: collection_state} = vnode.attrs.collection
        const use_virtual = Boolean(vnode.attrs.virtualize)
        const total = collection_state.total ?? collection_state.items.length
        // Nothing to count yet: the collection is still being set up — which is where the server leaves
        // it, since the rows themselves load in the browser — or its first page is in flight. A reload
        // that keeps its rows on screen (a filter change) is not this: those keep their count, with the
        // spinner beside it. The element renders either way, because its auto margin is what pushes the
        // controls to the end of the row; leaving it out until the first count lands makes them start at
        // the beginning of the row and jump across once it does.
        const counting = !collection_state.ready || (collection_state.loading && !collection_state.items.length)

        return (
            <div className={classes('c-collection-status', vnode.attrs.className)}>
                {(() => {
                    if (counting || collection_state.loading) {
                        return <Spinner />
                    }
                    return <Icon className='icon-d' name='storeSearch' type='default' />
                })()}
                <div className='indicator'>
                    {(() => {
                        if (counting) {
                            return <span className='counting'>{vnode.attrs.loading}</span>
                        }
                        if (use_virtual) {
                            // When virtualized the viewport range comes from CollectionItems.
                            const range = collection_state.view.viewport_range
                            const start_index = range.start
                            const end_index = range.end
                            return (
                                <>
                                    <span className='total-items'>{total} items</span>
                                    <span className='viewport-range'>{`${start_index}-${end_index}`}</span>
                                </>
                            )
                        }
                        if (collection_state.total === null) {
                            return collection_state.items.length
                        }
                        return [
                            <span>{collection_state.items.length}</span>,
                            <span>/</span>,
                            <span>{collection_state.total}</span>,
                        ]
                    })()}
                </div>
            </div>
        )
    }
}
